import { IModule } from '@/interfaces/powerMarkets/IModule.ts';
import { PeriodTypeOverridesWithInherited } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';
import { IReportBranding } from '@/interfaces/reports/IReportBranding.ts';
import { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { IDateObject } from '../IDateObject.ts';
import { IDatePeriod } from '../balancing/IDatePeriod.ts';

export enum DateOverrideComponents {
    'chart' = 'chart',
    'liveTable' = 'liveTable',
    'boa' = 'boa',
    'commonSettings' = 'commonSettings',
}

export interface IDashboard {
    colour: string;
    description: string;
    enactType: string;
    favourite: boolean;
    icon: string;
    id: string;
    modules: Array<IModule>;
    name: string;
    parentId: string | null;
    // TEMP dual-site: dual-written with dateOverrides.chart via
    // helpers/powerMarkets/dashboardDateOverrideLegacySettings.ts (delete with helper when old Enact is retired).
    fromToDateOverride?: IDateObject | null;
    dateOverrides?: IDashboardOverrides;
    reportBranding?: IReportBranding;
    reportSettings?: IDashboardReportSettings;
    // TEMP dual-site: root TZ dual-written by the same helper for Enact pageTimezoneOverride getters.
    // Spark reads timezone/periodsPerDay from dateOverrides.chart instead.
    // timezone: string | null;
    // periodsPerDay: number | null;
}

export interface IDashboardReportSettings {
    reportMode?: boolean;
}

export interface IDashboardCommonSettings {
    sharedTooltipAcrossModules?: boolean;
    sharedLegendToggling?: boolean;
    sharedNivEvolutionViewfinder?: boolean;
    useYAxisNormalisation?: boolean;
}

export type TDashboardStorageForecastOverrideKind = 'asset' | 'system';

export interface IDashboardStorageForecastContext {
    country?: TStorageForecastCountries | null;
    quarter?: string | null;
    scenarioId?: string | null;
    scenarioName?: string | null;
    scenarioLabel?: string | null;
    percentile?: string | null;
}

export type IDashboardStorageForecastCountryOverrides = Partial<Record<TStorageForecastCountries, IDashboardStorageForecastContext>>;

export interface IDashboardStorageForecastOverrides {
    asset?: IDashboardStorageForecastCountryOverrides;
    system?: IDashboardStorageForecastCountryOverrides;
}

export interface IDashboardOverrides {
    chart?: IDateObject;
    liveTable?: IDateObject;
    hof?: IDateObject;
    boa?: IDatePeriod | PeriodTypeOverridesWithInherited;
    commonSettings?: IDashboardCommonSettings;
    storageForecast?: IDashboardStorageForecastOverrides;
}

export type DashboardOverrideName = 'dayAheadDashboard' | 'monthlySummary' | 'bmDashboards' | 'epexDashboards' | 'intradayDashboards' | 'intradayTradingDashboards' | 'dayAheadDashboards' | 'ancillaryDashboards' | 'nivEvolutionDashboard' | 'europeanBalancing' | 'marketOverview' | 'interconnectorDashboards' | 'windForecasting' | 'Remit' | 'weatherForecasting' | 'forecastHistory' | 'ancillaryContracts' | 'assetMonitoring';

export type PageDates = Partial<Record<DashboardOverrideName, IDashboardOverrides>>;

export interface IDashboardWithNoModules extends Omit<IDashboard, 'modules'> {
    modules: never;
}
